import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { Observable, startWith, map } from 'rxjs';

interface BoQUnit {
  unit_id: number;
  unit_code: string;
  unit_name: string;
  full_form: string;
  meaning?: string;
  when_used?: string;
  example_usage?: string;
  unit_type: string;
  is_active: boolean;
  display_order: number;
}

interface BoQSection {
  section_id?: number;
  section_code: string;
  section_name: string;
  description?: string;
  sequence_order: number;
  parent_section_id?: number;
}

interface BoQItem {
  item_id?: number;
  section_id?: number;
  item_code: string;
  item_description: string;
  specification?: string;
  unit: string;
  quantity: number;
  material_unit_rate?: number;
  labor_unit_rate?: number;
  equipment_unit_rate?: number;
  other_costs?: number;
  wastage_percentage?: number;
  discount_percentage?: number;
  tax_percentage?: number;
  remarks?: string;
  sequence_order: number;
  breakdown?: BoQResourceInput[];
}

interface BoQResourceInput {
  breakdown_type: 'man' | 'machine' | 'material';
  description: string;
  quantity: number;
  unit?: string;
  unit_rate: number;
}

@Component({
  selector: 'app-boq-form',
  templateUrl: './boq-form.component.html',
  styleUrls: ['./boq-form.component.css']
})
export class BoqFormComponent implements OnInit {
  boqForm!: FormGroup;
  boqId: number | null = null;
  mode: 'create' | 'edit' = 'create';
  loading = false;
  submitting = false;

  // Data
  projects: any[] = [];
  selectedProject: any = null;
  units: BoQUnit[] = [];
  unitsByType: any = {};
  filteredUnits: BoQUnit[] = [];
  materials: any[] = [];
  currentEditingItemIndex: number = -1;
  expandedItems: { [key: number]: boolean } = {};
  lrsExpanded = false;
  Object = Object; // Expose Object to template

  // Assignment & review (edit mode)
  boqDetail: { assigned_to?: number; assigned_to_name?: string; estimation_status?: string } = {};
  staffList: any[] = [];
  reviewRequests: any[] = [];
  assignStaffId: number | null = null;
  assignSending = false;
  approveSending = false;
  submitSending = false;
  rejectSending = false;
  showReviewRequestForm = false;
  showRejectForm = false;
  rejectReason = '';
  reviewRequestType = 'update_field';
  reviewRequestDescription = '';
  reviewRequestSending = false;
  lrsRoutes: LrsRoute[] = [];
  private boqMetadata: Record<string, unknown> = {};

  toggleItemExpand(index: number): void {
    this.expandedItems[index] = !this.expandedItems[index];
  }

  isItemExpanded(index: number): boolean {
    return !!this.expandedItems[index];
  }

  getEffectiveUnitRate(index: number): number {
    const item = this.items.at(index);
    const material = parseFloat(item.get('material_unit_rate')?.value) || 0;
    const labor = parseFloat(item.get('labor_unit_rate')?.value) || 0;
    const equipment = parseFloat(item.get('equipment_unit_rate')?.value) || 0;
    const other = parseFloat(item.get('other_costs')?.value) || 0;
    return material + labor + equipment + other;
  }

  // Form arrays
  get sections(): FormArray {
    return this.boqForm.get('sections') as FormArray;
  }

  get items(): FormArray {
    return this.boqForm.get('items') as FormArray;
  }

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private lrsService: LrsService,
    private toastrService: ToastrMsgService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  openDeviation(): void {
    if (!this.boqId) return;
    this.router.navigate(['/home/boq', this.boqId, 'deviation']);
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['id'] && params['id'] !== 'new') {
        this.boqId = +params['id'];
        this.mode = 'edit';
        this.loadBoQ();
      } else {
        this.mode = 'create';
      }
    });

    this.initForm();
    this.loadProjects();
    this.loadUnits();
    this.loadMaterials();
  }

  initForm(): void {
    this.boqForm = this.fb.group({
      project_id: ['', Validators.required],
      boq_code: ['', Validators.required],
      boq_name: ['', Validators.required],
      description: [''],
      currency: ['INR'],
      exchange_rate: [1.0],
      overhead_percentage: [0],
      profit_percentage: [0],
      tax_percentage: [0],
      contingency_percentage: [0],
      branch_id: [''],
      lrs_route_id: [null as number | null],
      lrs_from_km: [null as number | null],
      lrs_to_km: [null as number | null],
      lrs_chainage: [''],
      sections: this.fb.array([]),
      items: this.fb.array([])
    });

    // Listen to project selection changes
    this.boqForm.get('project_id')?.valueChanges.subscribe(projectId => {
      this.resolveSelectedProject(projectId);
    });
    this.loadLrsRoutes();
  }

  get isRoadDomain(): boolean {
    const domain = String(this.selectedProject?.domain || '').toLowerCase();
    const subDomain = String(this.selectedProject?.sub_domain || '').toLowerCase();
    return domain === 'road' || subDomain.includes('road') || subDomain.includes('highway');
  }

  private clearLrsFields(): void {
    this.lrsExpanded = false;
    this.boqForm.patchValue({
      lrs_route_id: null,
      lrs_from_km: null,
      lrs_to_km: null,
      lrs_chainage: '',
    }, { emitEvent: false });
  }

  private resolveSelectedProject(projectId: number | string | null): void {
    if (!projectId) {
      this.selectedProject = null;
      this.clearLrsFields();
      return;
    }
    this.selectedProject = this.projects.find(p => p.id === projectId || String(p.id) === String(projectId)) || this.selectedProject;
    this.apiService.getProjectCommonId(projectId).subscribe({
      next: (res: any) => {
        const project = res.data || res;
        if (project) {
          this.selectedProject = { ...this.selectedProject, ...project };
          if (this.mode === 'create' && this.sections.length === 0 && this.items.length === 0) {
            this.loadDefaultBoQForProject(project);
          }
        }
        if (this.isRoadDomain) {
          this.loadLrsRoutes(String(projectId));
        } else {
          this.clearLrsFields();
        }
      },
      error: () => {
        if (!this.isRoadDomain) {
          this.clearLrsFields();
        }
      }
    });
  }

  loadLrsRoutes(projectId?: string): void {
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  openBoqOnMap(): void {
    const v = this.boqForm.value;
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (v.lrs_route_id) queryParams['route_id'] = v.lrs_route_id;
    if (v.lrs_from_km != null) queryParams['from_km'] = v.lrs_from_km;
    if (v.lrs_to_km != null) queryParams['to_km'] = v.lrs_to_km;
    if (v.project_id) queryParams['project_id'] = v.project_id;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  private parseMetadata(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    try {
      return JSON.parse(String(raw));
    } catch {
      return {};
    }
  }

  loadProjects(): void {
    this.apiService.getProjectCommonAll({ limit: 1000 }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.projects = res.data || [];
        } else {
          this.projects = res.data || res || [];
        }
      },
      error: (err: any) => {
        console.error('Error loading projects:', err);
        this.toastrService.error('Failed to load projects');
      }
    });
  }

  loadUnits(): void {
    this.apiService.getBoQUnits({ is_active: true }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.units = res.data || [];
          this.filteredUnits = this.units;
          // Group units by type
          this.unitsByType = this.units.reduce((acc: any, unit: BoQUnit) => {
            if (!acc[unit.unit_type]) {
              acc[unit.unit_type] = [];
            }
            acc[unit.unit_type].push(unit);
            return acc;
          }, {});
        }
      },
      error: (err: any) => {
        console.error('Error loading units:', err);
        this.toastrService.error('Failed to load units');
      }
    });
  }

  loadMaterials(): void {
    this.apiService.getMaterialsMaster().subscribe({
      next: (res: any) => {
        this.materials = res?.data || res || [];
      },
      error: (err: any) => {
        console.error('Error loading materials:', err);
        this.toastrService.error('Failed to load materials');
      }
    });
  }

  filterMaterials(value: string): any[] {
    if (!value || typeof value !== 'string') {
      return this.materials;
    }
    const filterValue = value.toLowerCase();
    return this.materials.filter(material => 
      (material.system_code && material.system_code.toLowerCase().includes(filterValue)) ||
      (material.name && material.name.toLowerCase().includes(filterValue))
    );
  }

  onMaterialSelected(event: any, itemIndex: number): void {
    const material = event.option.value;
    
    if (material && typeof material === 'object') {
      const itemForm = this.items.at(itemIndex);
      itemForm.patchValue({
        item_code: material.system_code || material.code || '',
        item_description: material.name || '',
        unit: material.unit || '',
        material_unit_rate: material.unit_cost || 0,
        specification: this.buildSpecification(material)
      }, { emitEvent: false });
    }
  }

  onMaterialInput(event: any, itemIndex: number): void {
    // Track which item is being edited
    this.currentEditingItemIndex = itemIndex;
  }

  buildSpecification(material: any): string {
    const specs: string[] = [];
    if (material.category) specs.push(`Category: ${material.category}`);
    if (material.subcategory) specs.push(`Subcategory: ${material.subcategory}`);
    if (material.size) specs.push(`Size: ${material.size}`);
    if (material.strength) specs.push(`Strength: ${material.strength}`);
    if (material.brand) specs.push(`Brand: ${material.brand}`);
    if (material.model) specs.push(`Model: ${material.model}`);
    return specs.join(', ');
  }

  displayMaterialFn(material: any): string {
    if (!material) return '';
    if (typeof material === 'string') return material;
    return material.system_code || material.code || material.name || '';
  }

  onProjectSelected(projectId: number): void {
    this.selectedProject = this.projects.find(p => p.id === projectId);
    
    // Fetch full project details to get area and domain
    if (projectId) {
      this.apiService.getProjectCommonId(projectId).subscribe({
        next: (res: any) => {
          const project = res.data || res;
          if (project) {
            this.selectedProject = { ...this.selectedProject, ...project };
            
            // Only load defaults if sections and items are empty (new BOQ)
            if (this.mode === 'create' && this.sections.length === 0 && this.items.length === 0) {
              this.loadDefaultBoQForProject(project);
            }
          }
        },
        error: (err: any) => {
          console.error('Error loading project details:', err);
        }
      });
    }
  }

  loadDefaultBoQForProject(project: any): void {
    const area = parseFloat(project.size) || parseFloat(project.area) || 0;
    const roadLength =
      parseFloat(project.road_length) || parseFloat(project.roadLength) || 0;
    const domain = String(project.domain || '').toLowerCase();
    const subDomain = String(project.sub_domain || '').toLowerCase();
    const budget = parseFloat(project.budget_amount) || 0;
    const scale =
      area > 0 ? area : budget > 0 ? Math.min(budget / 100, 50000) : 1000;

    const isRoadProject =
      roadLength > 0 ||
      domain.includes('road') ||
      subDomain.includes('road') ||
      subDomain.includes('highway');

    if (isRoadProject) {
      const roadArea = roadLength > 0 ? roadLength : scale;
      if (roadArea > 0) {
        this.loadDefaultRoadBoQ(roadArea, project);
        return;
      }
    }

    if (domain.includes('commercial') || subDomain.includes('commercial')) {
      this.loadDefaultCommercialBoQ(scale);
      return;
    }
    if (
      domain.includes('education') ||
      domain.includes('educational') ||
      subDomain.includes('education') ||
      subDomain.includes('educational')
    ) {
      this.loadDefaultEducationBoQ(scale);
      return;
    }
    if (
      domain.includes('gas') ||
      domain.includes('telecom') ||
      domain.includes('water') ||
      domain.includes('power') ||
      domain.includes('utilities')
    ) {
      this.loadDefaultCommercialBoQ(scale);
      return;
    }
    if (domain.includes('construction')) {
      if (subDomain.includes('commercial')) {
        this.loadDefaultCommercialBoQ(scale);
      } else if (
        subDomain.includes('education') ||
        subDomain.includes('educational')
      ) {
        this.loadDefaultEducationBoQ(scale);
      } else {
        this.loadDefaultResidentialBoQ(scale);
      }
      return;
    }

    // Residential + universal fallback for all other domains
    this.loadDefaultResidentialBoQ(scale);
  }

  loadDefaultResidentialBoQ(area: number): void {
    // Get default BOQ template based on area ranges
    const template = this.getResidentialBoQTemplate(area);
    
    if (template && template.sections && template.items) {
      // Clear existing sections and items
      while (this.sections.length !== 0) {
        this.sections.removeAt(0);
      }
      while (this.items.length !== 0) {
        this.items.removeAt(0);
      }

      // Add sections
      template.sections.forEach((section: any) => {
        this.addSection({
          section_code: section.section_code,
          section_name: section.section_name,
          description: section.description,
          sequence_order: section.sequence_order
        });
      });

      // Add items with calculated quantities based on area
      template.items.forEach((item: any) => {
        const quantity = this.calculateQuantityForArea(item, area);
        this.addItem({
          item_code: item.item_code,
          item_description: item.item_description,
          specification: item.specification,
          unit: item.unit,
          quantity: quantity,
          material_unit_rate: item.material_unit_rate || 0,
          labor_unit_rate: item.labor_unit_rate || 0,
          equipment_unit_rate: item.equipment_unit_rate || 0,
          other_costs: item.other_costs || 0,
          wastage_percentage: item.wastage_percentage || 0,
          discount_percentage: item.discount_percentage || 0,
          tax_percentage: item.tax_percentage || 0,
          sequence_order: item.sequence_order
        });
      });

      this.toastrService.success(`Default BOQ loaded for ${area} sq ft residential project`);
    }
  }

  getResidentialBoQTemplate(area: number): any {
    // Define area ranges and corresponding templates
    // Small: 0-1000 sq ft
    // Medium: 1001-3000 sq ft
    // Large: 3001-5000 sq ft
    // Very Large: 5001+ sq ft

    let template: any = {
      sections: [],
      items: []
    };

    if (area <= 1000) {
      template = this.getSmallResidentialTemplate(area);
    } else if (area <= 3000) {
      template = this.getMediumResidentialTemplate(area);
    } else if (area <= 5000) {
      template = this.getLargeResidentialTemplate(area);
    } else {
      template = this.getVeryLargeResidentialTemplate(area);
    }

    return template;
  }

  getSmallResidentialTemplate(area: number): any {
    // Quantity norms per sqft for Indian residential; rates in ₹ (approximate)
    const qty = (perSqft: number) => area * perSqft;
    return {
      sections: [
        { section_code: 'SEC-001', section_name: 'Earthwork', description: 'Excavation and filling', sequence_order: 1 },
        { section_code: 'SEC-002', section_name: 'Foundation', description: 'Foundation work', sequence_order: 2 },
        { section_code: 'SEC-003', section_name: 'Superstructure', description: 'Building structure', sequence_order: 3 },
        { section_code: 'SEC-004', section_name: 'Finishing', description: 'Interior and exterior finishing', sequence_order: 4 },
        { section_code: 'SEC-005', section_name: 'Services', description: 'Electrical, plumbing, sanitary', sequence_order: 5 }
      ],
      items: [
        // Earthwork (per sqft: excavation ~0.35 CUM, backfill ~0.18 CUM)
        { item_code: 'ITM-001', item_description: 'Excavation for foundation', unit: 'CUM', quantity: qty(0.35), material_unit_rate: 0, labor_unit_rate: 180, sequence_order: 1 },
        { item_code: 'ITM-002', item_description: 'Backfilling with approved earth', unit: 'CUM', quantity: qty(0.18), material_unit_rate: 0, labor_unit_rate: 120, sequence_order: 2 },
        // Foundation (PCC ~0.06, RCC ~0.18 CUM per sqft)
        { item_code: 'ITM-003', item_description: 'PCC (1:4:8) for base', unit: 'CUM', quantity: qty(0.06), material_unit_rate: 3800, labor_unit_rate: 220, sequence_order: 3 },
        { item_code: 'ITM-004', item_description: 'RCC M20 - Footing & plinth', unit: 'CUM', quantity: qty(0.08), material_unit_rate: 5800, labor_unit_rate: 320, sequence_order: 4 },
        { item_code: 'ITM-005', item_description: 'DPC - Damp proof course', unit: 'SQM', quantity: qty(0.95), material_unit_rate: 120, labor_unit_rate: 45, sequence_order: 5 },
        // Superstructure (brickwork ~0.24, RCC slab ~0.11 CUM per sqft)
        { item_code: 'ITM-006', item_description: 'Brickwork in CM 1:6 (9" wall)', unit: 'CUM', quantity: qty(0.24), material_unit_rate: 4800, labor_unit_rate: 280, sequence_order: 6 },
        { item_code: 'ITM-007', item_description: 'RCC M20 - Columns & beams', unit: 'CUM', quantity: qty(0.06), material_unit_rate: 6200, labor_unit_rate: 380, sequence_order: 7 },
        { item_code: 'ITM-008', item_description: 'RCC M20 - Slab', unit: 'CUM', quantity: qty(0.11), material_unit_rate: 6400, labor_unit_rate: 380, sequence_order: 8 },
        { item_code: 'ITM-009', item_description: 'RCC lintels', unit: 'CUM', quantity: qty(0.008), material_unit_rate: 6000, labor_unit_rate: 350, sequence_order: 9 },
        { item_code: 'ITM-010', item_description: 'Waterproofing - Terrace', unit: 'SQM', quantity: area, material_unit_rate: 165, labor_unit_rate: 75, sequence_order: 10 },
        // Finishing (plaster ~3.4, flooring ~0.85, painting ~3.2 SQM per sqft)
        { item_code: 'ITM-011', item_description: 'Plastering 1:6 (internal)', unit: 'SQM', quantity: qty(3.4), material_unit_rate: 50, labor_unit_rate: 38, sequence_order: 11 },
        { item_code: 'ITM-012', item_description: 'Flooring - Vitrified tiles', unit: 'SQM', quantity: qty(0.85), material_unit_rate: 95, labor_unit_rate: 45, sequence_order: 12 },
        { item_code: 'ITM-013', item_description: 'Painting - Interior', unit: 'SQM', quantity: qty(3.2), material_unit_rate: 28, labor_unit_rate: 22, sequence_order: 13 },
        { item_code: 'ITM-014', item_description: 'Doors - Teak wood', unit: 'NOS', quantity: Math.max(1, Math.ceil(area / 100)), material_unit_rate: 8500, labor_unit_rate: 800, sequence_order: 14 },
        { item_code: 'ITM-015', item_description: 'Windows - Aluminum', unit: 'NOS', quantity: Math.max(2, Math.ceil(area / 50)), material_unit_rate: 3200, labor_unit_rate: 400, sequence_order: 15 },
        // Services (electrical ~1 SQM, plumbing ~1.2 SQM coverage per sqft)
        { item_code: 'ITM-016', item_description: 'Electrical wiring & points', unit: 'SQM', quantity: area, material_unit_rate: 140, labor_unit_rate: 55, sequence_order: 16 },
        { item_code: 'ITM-017', item_description: 'Plumbing - Water supply & drainage', unit: 'SQM', quantity: area, material_unit_rate: 165, labor_unit_rate: 65, sequence_order: 17 },
        { item_code: 'ITM-018', item_description: 'Sanitary fixtures', unit: 'NOS', quantity: Math.max(1, Math.ceil(area / 150)), material_unit_rate: 4500, labor_unit_rate: 650, sequence_order: 18 }
      ]
    };
  }

  getMediumResidentialTemplate(area: number): any {
    const baseTemplate = this.getSmallResidentialTemplate(area);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      const qMult = ['NOS'].includes(item.unit) ? 1 : 1.08; // Scale quantities for medium (slightly more)
      const rateMult = 0.96; // Slightly better rates for bulk
      return {
        ...item,
        quantity: item.quantity * qMult,
        material_unit_rate: item.material_unit_rate * rateMult,
        labor_unit_rate: item.labor_unit_rate * rateMult
      };
    });
    return baseTemplate;
  }

  getLargeResidentialTemplate(area: number): any {
    const baseTemplate = this.getSmallResidentialTemplate(area);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      const qMult = ['NOS'].includes(item.unit) ? 1 : 1.12;
      const rateMult = 0.93;
      return {
        ...item,
        quantity: item.quantity * qMult,
        material_unit_rate: item.material_unit_rate * rateMult,
        labor_unit_rate: item.labor_unit_rate * rateMult
      };
    });
    return baseTemplate;
  }

  getVeryLargeResidentialTemplate(area: number): any {
    const baseTemplate = this.getSmallResidentialTemplate(area);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      const qMult = ['NOS'].includes(item.unit) ? 1 : 1.15;
      const rateMult = 0.90;
      return {
        ...item,
        quantity: item.quantity * qMult,
        material_unit_rate: item.material_unit_rate * rateMult,
        labor_unit_rate: item.labor_unit_rate * rateMult
      };
    });
    return baseTemplate;
  }

  calculateQuantityForArea(item: any, area: number): number {
    // If item has a base_quantity_per_sqft, use it
    if (item.base_quantity_per_sqft) {
      return item.base_quantity_per_sqft * area;
    }
    
    // Otherwise use the quantity from template (already calculated)
    return item.quantity || 0;
  }

  loadDefaultCommercialBoQ(area: number): void {
    // Get default BOQ template based on area ranges
    const template = this.getCommercialBoQTemplate(area);
    
    if (template && template.sections && template.items) {
      // Clear existing sections and items
      while (this.sections.length !== 0) {
        this.sections.removeAt(0);
      }
      while (this.items.length !== 0) {
        this.items.removeAt(0);
      }

      // Add sections
      template.sections.forEach((section: any) => {
        this.addSection({
          section_code: section.section_code,
          section_name: section.section_name,
          description: section.description,
          sequence_order: section.sequence_order
        });
      });

      // Add items with calculated quantities based on area
      template.items.forEach((item: any) => {
        const quantity = this.calculateQuantityForArea(item, area);
        this.addItem({
          item_code: item.item_code,
          item_description: item.item_description,
          specification: item.specification,
          unit: item.unit,
          quantity: quantity,
          material_unit_rate: item.material_unit_rate || 0,
          labor_unit_rate: item.labor_unit_rate || 0,
          equipment_unit_rate: item.equipment_unit_rate || 0,
          other_costs: item.other_costs || 0,
          wastage_percentage: item.wastage_percentage || 0,
          discount_percentage: item.discount_percentage || 0,
          tax_percentage: item.tax_percentage || 0,
          sequence_order: item.sequence_order
        });
      });

      this.toastrService.success(`Default BOQ loaded for ${area} sq ft commercial project`);
    }
  }

  getCommercialBoQTemplate(area: number): any {
    // Define area ranges and corresponding templates for commercial
    // Small: 0-5000 sq ft
    // Medium: 5001-15000 sq ft
    // Large: 15001-50000 sq ft
    // Very Large: 50001+ sq ft

    let template: any = {
      sections: [],
      items: []
    };

    if (area <= 5000) {
      template = this.getSmallCommercialTemplate(area);
    } else if (area <= 15000) {
      template = this.getMediumCommercialTemplate(area);
    } else if (area <= 50000) {
      template = this.getLargeCommercialTemplate(area);
    } else {
      template = this.getVeryLargeCommercialTemplate(area);
    }

    return template;
  }

  getSmallCommercialTemplate(area: number): any {
    const qty = (perSqft: number) => area * perSqft;
    const nos = (perArea: number) => Math.max(1, Math.ceil(area / perArea));
    return {
      sections: [
        { section_code: 'SEC-001', section_name: 'Site Preparation', description: 'Site clearing and preparation', sequence_order: 1 },
        { section_code: 'SEC-002', section_name: 'Earthwork', description: 'Excavation and filling', sequence_order: 2 },
        { section_code: 'SEC-003', section_name: 'Foundation & Structure', description: 'Foundation and structural work', sequence_order: 3 },
        { section_code: 'SEC-004', section_name: 'Building Envelope', description: 'Walls, roofing, facade', sequence_order: 4 },
        { section_code: 'SEC-005', section_name: 'Interior Finishing', description: 'Interior works and finishing', sequence_order: 5 },
        { section_code: 'SEC-006', section_name: 'MEP Services', description: 'Mechanical, Electrical, Plumbing', sequence_order: 6 },
        { section_code: 'SEC-007', section_name: 'HVAC System', description: 'Heating, Ventilation, Air Conditioning', sequence_order: 7 },
        { section_code: 'SEC-008', section_name: 'Fire Safety', description: 'Fire safety systems', sequence_order: 8 },
        { section_code: 'SEC-009', section_name: 'Elevators & Escalators', description: 'Vertical transportation', sequence_order: 9 },
        { section_code: 'SEC-010', section_name: 'External Works', description: 'Parking, landscaping, utilities', sequence_order: 10 }
      ],
      items: [
        // Site Preparation
        { item_code: 'ITM-001', item_description: 'Site clearing and leveling', unit: 'SQM', quantity: qty(1.2), material_unit_rate: 0, labor_unit_rate: 28, sequence_order: 1 },
        { item_code: 'ITM-002', item_description: 'Temporary fencing', unit: 'M', quantity: Math.max(20, Math.ceil(Math.sqrt(area) * 4)), material_unit_rate: 165, labor_unit_rate: 35, sequence_order: 2 },
        // Earthwork (deeper for commercial multi-storey)
        { item_code: 'ITM-003', item_description: 'Excavation for foundation', unit: 'CUM', quantity: qty(0.42), material_unit_rate: 0, labor_unit_rate: 195, sequence_order: 3 },
        { item_code: 'ITM-004', item_description: 'Backfilling and compaction', unit: 'CUM', quantity: qty(0.22), material_unit_rate: 0, labor_unit_rate: 130, sequence_order: 4 },
        // Foundation & Structure (heavier for commercial)
        { item_code: 'ITM-005', item_description: 'PCC (1:4:8) for foundation', unit: 'CUM', quantity: qty(0.08), material_unit_rate: 3950, labor_unit_rate: 235, sequence_order: 5 },
        { item_code: 'ITM-006', item_description: 'RCC M25 - Foundation', unit: 'CUM', quantity: qty(0.18), material_unit_rate: 6350, labor_unit_rate: 365, sequence_order: 6 },
        { item_code: 'ITM-007', item_description: 'RCC M25 - Columns & Beams', unit: 'CUM', quantity: qty(0.17), material_unit_rate: 6600, labor_unit_rate: 390, sequence_order: 7 },
        { item_code: 'ITM-008', item_description: 'RCC M25 - Slab', unit: 'CUM', quantity: qty(0.14), material_unit_rate: 6850, labor_unit_rate: 405, sequence_order: 8 },
        { item_code: 'ITM-009', item_description: 'DPC - Damp proof course', unit: 'SQM', quantity: qty(0.95), material_unit_rate: 135, labor_unit_rate: 48, sequence_order: 9 },
        // Building Envelope
        { item_code: 'ITM-010', item_description: 'Brickwork in CM 1:6', unit: 'CUM', quantity: qty(0.28), material_unit_rate: 4950, labor_unit_rate: 290, sequence_order: 10 },
        { item_code: 'ITM-011', item_description: 'Aluminum/UPVC Windows', unit: 'SQM', quantity: qty(0.16), material_unit_rate: 1280, labor_unit_rate: 165, sequence_order: 11 },
        { item_code: 'ITM-012', item_description: 'Glass Facade/Curtain Wall', unit: 'SQM', quantity: qty(0.18), material_unit_rate: 3650, labor_unit_rate: 420, sequence_order: 12 },
        { item_code: 'ITM-013', item_description: 'Waterproofing - Terrace', unit: 'SQM', quantity: area, material_unit_rate: 185, labor_unit_rate: 85, sequence_order: 13 },
        // Interior Finishing (premium)
        { item_code: 'ITM-014', item_description: 'Plastering 1:6', unit: 'SQM', quantity: qty(3.9), material_unit_rate: 52, labor_unit_rate: 42, sequence_order: 14 },
        { item_code: 'ITM-015', item_description: 'False Ceiling - Gypsum', unit: 'SQM', quantity: qty(0.94), material_unit_rate: 195, labor_unit_rate: 65, sequence_order: 15 },
        { item_code: 'ITM-016', item_description: 'Flooring - Vitrified tiles', unit: 'SQM', quantity: qty(0.88), material_unit_rate: 128, labor_unit_rate: 55, sequence_order: 16 },
        { item_code: 'ITM-017', item_description: 'Painting - Interior (Premium)', unit: 'SQM', quantity: qty(3.7), material_unit_rate: 48, labor_unit_rate: 32, sequence_order: 17 },
        { item_code: 'ITM-018', item_description: 'Painting - Exterior (Weatherproof)', unit: 'SQM', quantity: qty(0.42), material_unit_rate: 72, labor_unit_rate: 45, sequence_order: 18 },
        { item_code: 'ITM-019', item_description: 'Doors - Commercial grade', unit: 'NOS', quantity: nos(120), material_unit_rate: 6500, labor_unit_rate: 750, sequence_order: 19 },
        // MEP Services
        { item_code: 'ITM-020', item_description: 'Electrical wiring & distribution', unit: 'SQM', quantity: area, material_unit_rate: 195, labor_unit_rate: 75, sequence_order: 20 },
        { item_code: 'ITM-021', item_description: 'Lighting fixtures (LED)', unit: 'NOS', quantity: nos(6), material_unit_rate: 950, labor_unit_rate: 120, sequence_order: 21 },
        { item_code: 'ITM-022', item_description: 'Plumbing - Water supply', unit: 'SQM', quantity: area, material_unit_rate: 215, labor_unit_rate: 85, sequence_order: 22 },
        { item_code: 'ITM-023', item_description: 'Plumbing - Drainage', unit: 'SQM', quantity: area, material_unit_rate: 195, labor_unit_rate: 75, sequence_order: 23 },
        { item_code: 'ITM-024', item_description: 'Sanitary fixtures', unit: 'NOS', quantity: nos(180), material_unit_rate: 4200, labor_unit_rate: 580, sequence_order: 24 },
        // HVAC System
        { item_code: 'ITM-025', item_description: 'HVAC Ductwork', unit: 'SQM', quantity: qty(0.75), material_unit_rate: 480, labor_unit_rate: 165, sequence_order: 25 },
        { item_code: 'ITM-026', item_description: 'AC Units (VRV/VRF)', unit: 'TR', quantity: Math.max(1, Math.ceil(area / 140)), material_unit_rate: 48000, labor_unit_rate: 5500, sequence_order: 26 },
        { item_code: 'ITM-027', item_description: 'HVAC Controls & Automation', unit: 'LS', quantity: 1, material_unit_rate: area * 28, labor_unit_rate: area * 9, sequence_order: 27 },
        // Fire Safety (mandatory for commercial)
        { item_code: 'ITM-028', item_description: 'Fire sprinkler system', unit: 'SQM', quantity: area, material_unit_rate: 295, labor_unit_rate: 105, sequence_order: 28 },
        { item_code: 'ITM-029', item_description: 'Fire alarm system', unit: 'SQM', quantity: area, material_unit_rate: 165, labor_unit_rate: 65, sequence_order: 29 },
        { item_code: 'ITM-030', item_description: 'Fire extinguishers', unit: 'NOS', quantity: nos(400), material_unit_rate: 2800, labor_unit_rate: 220, sequence_order: 30 },
        // Elevators
        { item_code: 'ITM-031', item_description: 'Passenger Elevator', unit: 'NOS', quantity: Math.max(1, Math.ceil(area / 10000)), material_unit_rate: 900000, labor_unit_rate: 110000, sequence_order: 31 },
        { item_code: 'ITM-032', item_description: 'Service Elevator', unit: 'NOS', quantity: area >= 15000 ? Math.ceil(area / 18000) : 0, material_unit_rate: 700000, labor_unit_rate: 85000, sequence_order: 32 },
        // External Works
        { item_code: 'ITM-033', item_description: 'Parking area - Paved', unit: 'SQM', quantity: qty(0.48), material_unit_rate: 375, labor_unit_rate: 85, sequence_order: 33 },
        { item_code: 'ITM-034', item_description: 'Landscaping', unit: 'SQM', quantity: qty(0.28), material_unit_rate: 135, labor_unit_rate: 45, sequence_order: 34 },
        { item_code: 'ITM-035', item_description: 'External lighting', unit: 'NOS', quantity: Math.max(4, Math.ceil(Math.sqrt(area) * 2)), material_unit_rate: 1350, labor_unit_rate: 165, sequence_order: 35 }
      ]
    };
  }

  getMediumCommercialTemplate(area: number): any {
    const baseTemplate = this.getSmallCommercialTemplate(area);
    const noScaleUnits = ['NOS', 'TR', 'LS'];
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-031' || item.item_code === 'ITM-032') return item;
      const scaleQty = !noScaleUnits.includes(item.unit);
      return {
        ...item,
        quantity: scaleQty ? item.quantity * 1.1 : item.quantity,
        material_unit_rate: item.material_unit_rate * 0.94,
        labor_unit_rate: item.labor_unit_rate * 0.94
      };
    });
    return baseTemplate;
  }

  getLargeCommercialTemplate(area: number): any {
    const baseTemplate = this.getSmallCommercialTemplate(area);
    const noScaleUnits = ['NOS', 'TR', 'LS'];
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-031' || item.item_code === 'ITM-032') return item;
      const scaleQty = !noScaleUnits.includes(item.unit);
      return {
        ...item,
        quantity: scaleQty ? item.quantity * 1.18 : item.quantity,
        material_unit_rate: item.material_unit_rate * 0.90,
        labor_unit_rate: item.labor_unit_rate * 0.90
      };
    });
    return baseTemplate;
  }

  getVeryLargeCommercialTemplate(area: number): any {
    const baseTemplate = this.getSmallCommercialTemplate(area);
    const noScaleUnits = ['NOS', 'TR', 'LS'];
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-031' || item.item_code === 'ITM-032') return item;
      const scaleQty = !noScaleUnits.includes(item.unit);
      return {
        ...item,
        quantity: scaleQty ? item.quantity * 1.25 : item.quantity,
        material_unit_rate: item.material_unit_rate * 0.86,
        labor_unit_rate: item.labor_unit_rate * 0.86
      };
    });
    return baseTemplate;
  }

  loadDefaultEducationBoQ(area: number): void {
    // Get default BOQ template based on area ranges
    const template = this.getEducationBoQTemplate(area);
    
    if (template && template.sections && template.items) {
      // Clear existing sections and items
      while (this.sections.length !== 0) {
        this.sections.removeAt(0);
      }
      while (this.items.length !== 0) {
        this.items.removeAt(0);
      }

      // Add sections
      template.sections.forEach((section: any) => {
        this.addSection({
          section_code: section.section_code,
          section_name: section.section_name,
          description: section.description,
          sequence_order: section.sequence_order
        });
      });

      // Add items with calculated quantities based on area
      template.items.forEach((item: any) => {
        const quantity = this.calculateQuantityForArea(item, area);
        this.addItem({
          item_code: item.item_code,
          item_description: item.item_description,
          specification: item.specification,
          unit: item.unit,
          quantity: quantity,
          material_unit_rate: item.material_unit_rate || 0,
          labor_unit_rate: item.labor_unit_rate || 0,
          equipment_unit_rate: item.equipment_unit_rate || 0,
          other_costs: item.other_costs || 0,
          wastage_percentage: item.wastage_percentage || 0,
          discount_percentage: item.discount_percentage || 0,
          tax_percentage: item.tax_percentage || 0,
          sequence_order: item.sequence_order
        });
      });

      this.toastrService.success(`Default BOQ loaded for ${area} sq ft education project`);
    }
  }

  getEducationBoQTemplate(area: number): any {
    // Define area ranges and corresponding templates for education
    // Small: 0-10000 sq ft (Small school)
    // Medium: 10001-50000 sq ft (Medium school/college)
    // Large: 50001-150000 sq ft (Large school/college)
    // Very Large: 150001+ sq ft (University/Institution)

    let template: any = {
      sections: [],
      items: []
    };

    if (area <= 10000) {
      template = this.getSmallEducationTemplate(area);
    } else if (area <= 50000) {
      template = this.getMediumEducationTemplate(area);
    } else if (area <= 150000) {
      template = this.getLargeEducationTemplate(area);
    } else {
      template = this.getVeryLargeEducationTemplate(area);
    }

    return template;
  }

  getSmallEducationTemplate(area: number): any {
    return {
      sections: [
        { section_code: 'SEC-001', section_name: 'Site Preparation', description: 'Site clearing and preparation', sequence_order: 1 },
        { section_code: 'SEC-002', section_name: 'Earthwork', description: 'Excavation and filling', sequence_order: 2 },
        { section_code: 'SEC-003', section_name: 'Foundation & Structure', description: 'Foundation and structural work', sequence_order: 3 },
        { section_code: 'SEC-004', section_name: 'Building Envelope', description: 'Walls, roofing, facade', sequence_order: 4 },
        { section_code: 'SEC-005', section_name: 'Classroom Finishing', description: 'Classroom interior works', sequence_order: 5 },
        { section_code: 'SEC-006', section_name: 'Laboratory Setup', description: 'Science and computer labs', sequence_order: 6 },
        { section_code: 'SEC-007', section_name: 'Library & Common Areas', description: 'Library, auditorium, common spaces', sequence_order: 7 },
        { section_code: 'SEC-008', section_name: 'Sports Facilities', description: 'Sports courts and facilities', sequence_order: 8 },
        { section_code: 'SEC-009', section_name: 'MEP Services', description: 'Mechanical, Electrical, Plumbing', sequence_order: 9 },
        { section_code: 'SEC-010', section_name: 'Fire Safety & Security', description: 'Fire safety and security systems', sequence_order: 10 },
        { section_code: 'SEC-011', section_name: 'External Works', description: 'Playground, parking, landscaping', sequence_order: 11 }
      ],
      items: [
        // Site Preparation
        { item_code: 'ITM-001', item_description: 'Site clearing and leveling', unit: 'SQM', quantity: area * 1.3, material_unit_rate: 0, labor_unit_rate: 25, sequence_order: 1 },
        { item_code: 'ITM-002', item_description: 'Temporary fencing and security', unit: 'M', quantity: Math.sqrt(area) * 4, material_unit_rate: 180, labor_unit_rate: 35, sequence_order: 2 },
        
        // Earthwork
        { item_code: 'ITM-003', item_description: 'Excavation for foundation', unit: 'CUM', quantity: area * 0.35, material_unit_rate: 0, labor_unit_rate: 170, sequence_order: 3 },
        { item_code: 'ITM-004', item_description: 'Backfilling and compaction', unit: 'CUM', quantity: area * 0.22, material_unit_rate: 0, labor_unit_rate: 115, sequence_order: 4 },
        
        // Foundation & Structure
        { item_code: 'ITM-005', item_description: 'PCC (1:4:8) for foundation', unit: 'CUM', quantity: area * 0.07, material_unit_rate: 3700, labor_unit_rate: 210, sequence_order: 5 },
        { item_code: 'ITM-006', item_description: 'RCC Foundation (M25)', unit: 'CUM', quantity: area * 0.18, material_unit_rate: 6100, labor_unit_rate: 340, sequence_order: 6 },
        { item_code: 'ITM-007', item_description: 'RCC Columns & Beams (M25)', unit: 'CUM', quantity: area * 0.16, material_unit_rate: 6400, labor_unit_rate: 370, sequence_order: 7 },
        { item_code: 'ITM-008', item_description: 'RCC Slab (M25)', unit: 'CUM', quantity: area * 0.13, material_unit_rate: 6700, labor_unit_rate: 390, sequence_order: 8 },
        
        // Building Envelope
        { item_code: 'ITM-009', item_description: 'Brickwork in CM 1:6', unit: 'CUM', quantity: area * 0.28, material_unit_rate: 4700, labor_unit_rate: 270, sequence_order: 9 },
        { item_code: 'ITM-010', item_description: 'Aluminum/UPVC Windows', unit: 'SQM', quantity: area * 0.18, material_unit_rate: 1100, labor_unit_rate: 140, sequence_order: 10 },
        { item_code: 'ITM-011', item_description: 'Doors - Classrooms & Offices', unit: 'NOS', quantity: area / 25, material_unit_rate: 4500, labor_unit_rate: 600, sequence_order: 11 },
        { item_code: 'ITM-012', item_description: 'Waterproofing - Terrace', unit: 'SQM', quantity: area, material_unit_rate: 175, labor_unit_rate: 75, sequence_order: 12 },
        
        // Classroom Finishing
        { item_code: 'ITM-013', item_description: 'Plastering (1:6)', unit: 'SQM', quantity: area * 3.8, material_unit_rate: 48, labor_unit_rate: 38, sequence_order: 13 },
        { item_code: 'ITM-014', item_description: 'False Ceiling - Gypsum', unit: 'SQM', quantity: area * 0.92, material_unit_rate: 170, labor_unit_rate: 55, sequence_order: 14 },
        { item_code: 'ITM-015', item_description: 'Flooring - Vitrified tiles (Classrooms)', unit: 'SQM', quantity: area * 0.75, material_unit_rate: 100, labor_unit_rate: 45, sequence_order: 15 },
        { item_code: 'ITM-016', item_description: 'Flooring - Anti-skid tiles (Corridors)', unit: 'SQM', quantity: area * 0.15, material_unit_rate: 85, labor_unit_rate: 40, sequence_order: 16 },
        { item_code: 'ITM-017', item_description: 'Painting - Interior (Washable)', unit: 'SQM', quantity: area * 3.6, material_unit_rate: 40, labor_unit_rate: 28, sequence_order: 17 },
        { item_code: 'ITM-018', item_description: 'Blackboard/Whiteboard installation', unit: 'NOS', quantity: area / 50, material_unit_rate: 2500, labor_unit_rate: 300, sequence_order: 18 },
        
        // Laboratory Setup
        { item_code: 'ITM-019', item_description: 'Lab flooring - Chemical resistant', unit: 'SQM', quantity: area * 0.08, material_unit_rate: 350, labor_unit_rate: 80, sequence_order: 19 },
        { item_code: 'ITM-020', item_description: 'Lab workbenches & fixtures', unit: 'NOS', quantity: area / 15, material_unit_rate: 12000, labor_unit_rate: 1500, sequence_order: 20 },
        { item_code: 'ITM-021', item_description: 'Lab exhaust system', unit: 'NOS', quantity: area / 100, material_unit_rate: 25000, labor_unit_rate: 3000, sequence_order: 21 },
        { item_code: 'ITM-022', item_description: 'Lab safety equipment', unit: 'LS', quantity: 1, material_unit_rate: area * 15, labor_unit_rate: area * 3, sequence_order: 22 },
        
        // Library & Common Areas
        { item_code: 'ITM-023', item_description: 'Library flooring - Carpet', unit: 'SQM', quantity: area * 0.05, material_unit_rate: 450, labor_unit_rate: 60, sequence_order: 23 },
        { item_code: 'ITM-024', item_description: 'Library shelving & furniture', unit: 'LS', quantity: 1, material_unit_rate: area * 120, labor_unit_rate: area * 20, sequence_order: 24 },
        { item_code: 'ITM-025', item_description: 'Auditorium seating', unit: 'NOS', quantity: area / 1.5, material_unit_rate: 3500, labor_unit_rate: 400, sequence_order: 25 },
        { item_code: 'ITM-026', item_description: 'Auditorium stage & lighting', unit: 'LS', quantity: 1, material_unit_rate: area * 80, labor_unit_rate: area * 15, sequence_order: 26 },
        
        // Sports Facilities
        { item_code: 'ITM-027', item_description: 'Basketball/Volleyball court', unit: 'NOS', quantity: Math.ceil(area / 20000), material_unit_rate: 250000, labor_unit_rate: 50000, sequence_order: 27 },
        { item_code: 'ITM-028', item_description: 'Sports equipment storage', unit: 'SQM', quantity: area * 0.02, material_unit_rate: 3000, labor_unit_rate: 500, sequence_order: 28 },
        
        // MEP Services
        { item_code: 'ITM-029', item_description: 'Electrical wiring & distribution', unit: 'SQM', quantity: area * 1.3, material_unit_rate: 170, labor_unit_rate: 65, sequence_order: 29 },
        { item_code: 'ITM-030', item_description: 'Lighting fixtures (LED)', unit: 'NOS', quantity: area / 7, material_unit_rate: 750, labor_unit_rate: 90, sequence_order: 30 },
        { item_code: 'ITM-031', item_description: 'Plumbing - Water supply', unit: 'SQM', quantity: area * 1.2, material_unit_rate: 190, labor_unit_rate: 75, sequence_order: 31 },
        { item_code: 'ITM-032', item_description: 'Plumbing - Drainage', unit: 'SQM', quantity: area * 1.1, material_unit_rate: 175, labor_unit_rate: 65, sequence_order: 32 },
        { item_code: 'ITM-033', item_description: 'Sanitary fixtures', unit: 'NOS', quantity: area / 150, material_unit_rate: 3200, labor_unit_rate: 450, sequence_order: 33 },
        { item_code: 'ITM-034', item_description: 'Drinking water fountains', unit: 'NOS', quantity: area / 500, material_unit_rate: 8500, labor_unit_rate: 800, sequence_order: 34 },
        
        // Fire Safety & Security
        { item_code: 'ITM-035', item_description: 'Fire sprinkler system', unit: 'SQM', quantity: area, material_unit_rate: 270, labor_unit_rate: 95, sequence_order: 35 },
        { item_code: 'ITM-036', item_description: 'Fire alarm system', unit: 'SQM', quantity: area, material_unit_rate: 145, labor_unit_rate: 55, sequence_order: 36 },
        { item_code: 'ITM-037', item_description: 'Fire extinguishers', unit: 'NOS', quantity: area / 400, material_unit_rate: 2400, labor_unit_rate: 180, sequence_order: 37 },
        { item_code: 'ITM-038', item_description: 'CCTV & Security system', unit: 'LS', quantity: 1, material_unit_rate: area * 35, labor_unit_rate: area * 10, sequence_order: 38 },
        
        // External Works
        { item_code: 'ITM-039', item_description: 'Playground - Paved area', unit: 'SQM', quantity: area * 0.8, material_unit_rate: 320, labor_unit_rate: 70, sequence_order: 39 },
        { item_code: 'ITM-040', item_description: 'Parking area', unit: 'SQM', quantity: area * 0.4, material_unit_rate: 330, labor_unit_rate: 75, sequence_order: 40 },
        { item_code: 'ITM-041', item_description: 'Landscaping & Green areas', unit: 'SQM', quantity: area * 0.5, material_unit_rate: 110, labor_unit_rate: 35, sequence_order: 41 },
        { item_code: 'ITM-042', item_description: 'External lighting', unit: 'NOS', quantity: Math.sqrt(area) * 2.5, material_unit_rate: 1100, labor_unit_rate: 140, sequence_order: 42 },
        { item_code: 'ITM-043', item_description: 'Boundary wall & gates', unit: 'M', quantity: Math.sqrt(area) * 4, material_unit_rate: 1200, labor_unit_rate: 200, sequence_order: 43 }
      ]
    };
  }

  getMediumEducationTemplate(area: number): any {
    const baseTemplate = this.getSmallEducationTemplate(area);
    // Scale items appropriately for medium size
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      // Special items that scale differently
      if (item.item_code === 'ITM-027' || item.item_code === 'ITM-025') {
        return item; // Keep court and seating quantities as calculated
      }
      return {
        ...item,
        quantity: item.quantity * 1.12, // 12% more for medium
        material_unit_rate: item.material_unit_rate * 0.93 // Better rates for bulk
      };
    });
    return baseTemplate;
  }

  getLargeEducationTemplate(area: number): any {
    const baseTemplate = this.getSmallEducationTemplate(area);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-027' || item.item_code === 'ITM-025') {
        return item;
      }
      return {
        ...item,
        quantity: item.quantity * 1.25,
        material_unit_rate: item.material_unit_rate * 0.89
      };
    });
    return baseTemplate;
  }

  getVeryLargeEducationTemplate(area: number): any {
    const baseTemplate = this.getSmallEducationTemplate(area);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-027' || item.item_code === 'ITM-025') {
        return item;
      }
      return {
        ...item,
        quantity: item.quantity * 1.38,
        material_unit_rate: item.material_unit_rate * 0.86
      };
    });
    return baseTemplate;
  }

  loadDefaultRoadBoQ(roadLength: number, project: any): void {
    // Get default BOQ template based on road length/area
    const template = this.getRoadBoQTemplate(roadLength, project);
    
    if (template && template.sections && template.items) {
      // Clear existing sections and items
      while (this.sections.length !== 0) {
        this.sections.removeAt(0);
      }
      while (this.items.length !== 0) {
        this.items.removeAt(0);
      }

      // Add sections
      template.sections.forEach((section: any) => {
        this.addSection({
          section_code: section.section_code,
          section_name: section.section_name,
          description: section.description,
          sequence_order: section.sequence_order
        });
      });

      // Add items with calculated quantities based on road length
      template.items.forEach((item: any) => {
        const quantity = this.calculateQuantityForRoad(item, roadLength, project);
        this.addItem({
          item_code: item.item_code,
          item_description: item.item_description,
          specification: item.specification,
          unit: item.unit,
          quantity: quantity,
          material_unit_rate: item.material_unit_rate || 0,
          labor_unit_rate: item.labor_unit_rate || 0,
          equipment_unit_rate: item.equipment_unit_rate || 0,
          other_costs: item.other_costs || 0,
          wastage_percentage: item.wastage_percentage || 0,
          discount_percentage: item.discount_percentage || 0,
          tax_percentage: item.tax_percentage || 0,
          sequence_order: item.sequence_order
        });
      });

      const unit = project.road_length ? 'km' : 'sq ft';
      this.toastrService.success(`Default BOQ loaded for ${roadLength} ${unit} road project`);
    }
  }

  getRoadBoQTemplate(roadLength: number, project: any): any {
    // Define road length ranges and corresponding templates
    // Small: 0-5 km (or 0-50000 sq ft)
    // Medium: 5-15 km (or 50001-150000 sq ft)
    // Large: 15-50 km (or 150001-500000 sq ft)
    // Very Large: 50+ km (or 500001+ sq ft)

    let template: any = {
      sections: [],
      items: []
    };

    // Determine if using km or area
    const isKm = project.road_length || project.roadLength;
    const threshold1 = isKm ? 5 : 50000;
    const threshold2 = isKm ? 15 : 150000;
    const threshold3 = isKm ? 50 : 500000;

    if (roadLength <= threshold1) {
      template = this.getSmallRoadTemplate(roadLength, project);
    } else if (roadLength <= threshold2) {
      template = this.getMediumRoadTemplate(roadLength, project);
    } else if (roadLength <= threshold3) {
      template = this.getLargeRoadTemplate(roadLength, project);
    } else {
      template = this.getVeryLargeRoadTemplate(roadLength, project);
    }

    return template;
  }

  getSmallRoadTemplate(roadLength: number, project: any): any {
    // Get road width from project metadata or default to 7m (2-lane)
    const roadWidth = parseFloat(project.road_width) || parseFloat(project.roadWidth) || parseFloat(project.lane_count) * 3.5 || 7;
    const isKm = project.road_length || project.roadLength;
    const lengthInMeters = isKm ? roadLength * 1000 : Math.sqrt(roadLength); // Approximate if area
    const widthInMeters = roadWidth;
    const areaInSqm = lengthInMeters * widthInMeters;

    return {
      sections: [
        { section_code: 'SEC-001', section_name: 'Site Preparation', description: 'Clearing, grubbing, and site preparation', sequence_order: 1 },
        { section_code: 'SEC-002', section_name: 'Earthwork', description: 'Excavation, embankment, and earthwork', sequence_order: 2 },
        { section_code: 'SEC-003', section_name: 'Subgrade Preparation', description: 'Subgrade treatment and compaction', sequence_order: 3 },
        { section_code: 'SEC-004', section_name: 'Granular Sub Base (GSB)', description: 'GSB layer construction', sequence_order: 4 },
        { section_code: 'SEC-005', section_name: 'Wet Mix Macadam (WMM)', description: 'WMM base course', sequence_order: 5 },
        { section_code: 'SEC-006', section_name: 'Bituminous Layers', description: 'BM, DBM, and BC layers', sequence_order: 6 },
        { section_code: 'SEC-007', section_name: 'Drainage Works', description: 'Side drains, culverts, and drainage', sequence_order: 7 },
        { section_code: 'SEC-008', section_name: 'Road Markings & Signage', description: 'Road markings and traffic signs', sequence_order: 8 },
        { section_code: 'SEC-009', section_name: 'Road Furniture', description: 'Guardrails, barriers, and road furniture', sequence_order: 9 },
        { section_code: 'SEC-010', section_name: 'Landscaping & Shoulders', description: 'Shoulder construction and landscaping', sequence_order: 10 }
      ],
      items: [
        // Site Preparation
        { item_code: 'ITM-001', item_description: 'Clearing and grubbing', unit: 'SQM', quantity: areaInSqm * 1.2, material_unit_rate: 0, labor_unit_rate: 15, sequence_order: 1 },
        { item_code: 'ITM-002', item_description: 'Removal of trees and stumps', unit: 'NOS', quantity: lengthInMeters / 20, material_unit_rate: 0, labor_unit_rate: 800, sequence_order: 2 },
        
        // Earthwork
        { item_code: 'ITM-003', item_description: 'Excavation in ordinary soil', unit: 'CUM', quantity: areaInSqm * 0.5, material_unit_rate: 0, labor_unit_rate: 120, sequence_order: 3 },
        { item_code: 'ITM-004', item_description: 'Excavation in hard rock', unit: 'CUM', quantity: areaInSqm * 0.1, material_unit_rate: 0, labor_unit_rate: 450, equipment_unit_rate: 800, sequence_order: 4 },
        { item_code: 'ITM-005', item_description: 'Embankment with approved soil', unit: 'CUM', quantity: areaInSqm * 0.4, material_unit_rate: 0, labor_unit_rate: 100, equipment_unit_rate: 150, sequence_order: 5 },
        { item_code: 'ITM-006', item_description: 'Compaction of embankment', unit: 'CUM', quantity: areaInSqm * 0.4, material_unit_rate: 0, labor_unit_rate: 30, equipment_unit_rate: 80, sequence_order: 6 },
        
        // Subgrade Preparation
        { item_code: 'ITM-007', item_description: 'Subgrade preparation and compaction', unit: 'SQM', quantity: areaInSqm, material_unit_rate: 0, labor_unit_rate: 25, equipment_unit_rate: 60, sequence_order: 7 },
        { item_code: 'ITM-008', item_description: 'Subgrade treatment with lime', unit: 'SQM', quantity: areaInSqm * 0.3, material_unit_rate: 35, labor_unit_rate: 20, sequence_order: 8 },
        
        // GSB
        { item_code: 'ITM-009', item_description: 'GSB Layer - 200mm', unit: 'CUM', quantity: areaInSqm * 0.2, material_unit_rate: 850, labor_unit_rate: 80, equipment_unit_rate: 120, sequence_order: 9 },
        { item_code: 'ITM-010', item_description: 'GSB Layer - 150mm', unit: 'CUM', quantity: areaInSqm * 0.15, material_unit_rate: 850, labor_unit_rate: 80, equipment_unit_rate: 120, sequence_order: 10 },
        
        // WMM
        { item_code: 'ITM-011', item_description: 'WMM Base Course - 250mm', unit: 'CUM', quantity: areaInSqm * 0.25, material_unit_rate: 1200, labor_unit_rate: 100, equipment_unit_rate: 150, sequence_order: 11 },
        { item_code: 'ITM-012', item_description: 'WMM Base Course - 200mm', unit: 'CUM', quantity: areaInSqm * 0.2, material_unit_rate: 1200, labor_unit_rate: 100, equipment_unit_rate: 150, sequence_order: 12 },
        
        // Bituminous Layers
        { item_code: 'ITM-013', item_description: 'Prime Coat', unit: 'SQM', quantity: areaInSqm, material_unit_rate: 45, labor_unit_rate: 15, equipment_unit_rate: 25, sequence_order: 13 },
        { item_code: 'ITM-014', item_description: 'Tack Coat', unit: 'SQM', quantity: areaInSqm * 2, material_unit_rate: 25, labor_unit_rate: 10, equipment_unit_rate: 15, sequence_order: 14 },
        { item_code: 'ITM-015', item_description: 'Bituminous Macadam (BM) - 50mm', unit: 'SQM', quantity: areaInSqm, material_unit_rate: 180, labor_unit_rate: 40, equipment_unit_rate: 80, sequence_order: 15 },
        { item_code: 'ITM-016', item_description: 'Dense Bituminous Macadam (DBM) - 50mm', unit: 'SQM', quantity: areaInSqm, material_unit_rate: 220, labor_unit_rate: 45, equipment_unit_rate: 90, sequence_order: 16 },
        { item_code: 'ITM-017', item_description: 'Bituminous Concrete (BC) - 40mm', unit: 'SQM', quantity: areaInSqm, material_unit_rate: 280, labor_unit_rate: 50, equipment_unit_rate: 100, sequence_order: 17 },
        
        // Drainage Works
        { item_code: 'ITM-018', item_description: 'Side drain - Excavation', unit: 'CUM', quantity: lengthInMeters * 2 * 0.5, material_unit_rate: 0, labor_unit_rate: 100, sequence_order: 18 },
        { item_code: 'ITM-019', item_description: 'Side drain - PCC lining', unit: 'CUM', quantity: lengthInMeters * 2 * 0.15, material_unit_rate: 3800, labor_unit_rate: 200, sequence_order: 19 },
        { item_code: 'ITM-020', item_description: 'Culvert - RCC pipe 900mm', unit: 'M', quantity: lengthInMeters / 100, material_unit_rate: 3500, labor_unit_rate: 500, equipment_unit_rate: 300, sequence_order: 20 },
        { item_code: 'ITM-021', item_description: 'Culvert - RCC box 2m x 2m', unit: 'M', quantity: lengthInMeters / 200, material_unit_rate: 12000, labor_unit_rate: 1500, equipment_unit_rate: 800, sequence_order: 21 },
        { item_code: 'ITM-022', item_description: 'Catch pits and manholes', unit: 'NOS', quantity: lengthInMeters / 50, material_unit_rate: 8500, labor_unit_rate: 1200, sequence_order: 22 },
        
        // Road Markings & Signage
        { item_code: 'ITM-023', item_description: 'Road marking - Center line', unit: 'M', quantity: lengthInMeters, material_unit_rate: 45, labor_unit_rate: 20, sequence_order: 23 },
        { item_code: 'ITM-024', item_description: 'Road marking - Edge lines', unit: 'M', quantity: lengthInMeters * 2, material_unit_rate: 45, labor_unit_rate: 20, sequence_order: 24 },
        { item_code: 'ITM-025', item_description: 'Road marking - Zebra crossing', unit: 'NOS', quantity: lengthInMeters / 500, material_unit_rate: 2500, labor_unit_rate: 400, sequence_order: 25 },
        { item_code: 'ITM-026', item_description: 'Traffic signs - Warning signs', unit: 'NOS', quantity: lengthInMeters / 200, material_unit_rate: 3500, labor_unit_rate: 500, sequence_order: 26 },
        { item_code: 'ITM-027', item_description: 'Traffic signs - Regulatory signs', unit: 'NOS', quantity: lengthInMeters / 300, material_unit_rate: 3200, labor_unit_rate: 450, sequence_order: 27 },
        { item_code: 'ITM-028', item_description: 'Road studs/Reflectors', unit: 'NOS', quantity: lengthInMeters * 4, material_unit_rate: 85, labor_unit_rate: 15, sequence_order: 28 },
        
        // Road Furniture
        { item_code: 'ITM-029', item_description: 'Guardrails - Steel', unit: 'M', quantity: lengthInMeters * 0.3, material_unit_rate: 1200, labor_unit_rate: 200, sequence_order: 29 },
        { item_code: 'ITM-030', item_description: 'Crash barriers - Concrete', unit: 'M', quantity: lengthInMeters * 0.2, material_unit_rate: 1800, labor_unit_rate: 300, sequence_order: 30 },
        { item_code: 'ITM-031', item_description: 'Roadside barriers', unit: 'M', quantity: lengthInMeters * 0.4, material_unit_rate: 450, labor_unit_rate: 100, sequence_order: 31 },
        { item_code: 'ITM-032', item_description: 'Street lighting poles', unit: 'NOS', quantity: lengthInMeters / 30, material_unit_rate: 8500, labor_unit_rate: 800, sequence_order: 32 },
        { item_code: 'ITM-033', item_description: 'LED street lights', unit: 'NOS', quantity: lengthInMeters / 30, material_unit_rate: 4500, labor_unit_rate: 300, sequence_order: 33 },
        
        // Landscaping & Shoulders
        { item_code: 'ITM-034', item_description: 'Shoulder construction - Earthen', unit: 'SQM', quantity: lengthInMeters * widthInMeters * 0.3, material_unit_rate: 0, labor_unit_rate: 35, equipment_unit_rate: 50, sequence_order: 34 },
        { item_code: 'ITM-035', item_description: 'Shoulder construction - Paved', unit: 'SQM', quantity: lengthInMeters * widthInMeters * 0.2, material_unit_rate: 180, labor_unit_rate: 40, equipment_unit_rate: 60, sequence_order: 35 },
        { item_code: 'ITM-036', item_description: 'Landscaping and turfing', unit: 'SQM', quantity: areaInSqm * 0.4, material_unit_rate: 45, labor_unit_rate: 25, sequence_order: 36 },
        { item_code: 'ITM-037', item_description: 'Tree plantation', unit: 'NOS', quantity: lengthInMeters / 10, material_unit_rate: 350, labor_unit_rate: 150, sequence_order: 37 }
      ]
    };
  }

  getMediumRoadTemplate(roadLength: number, project: any): any {
    const baseTemplate = this.getSmallRoadTemplate(roadLength, project);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      // Special items that scale differently
      if (item.item_code === 'ITM-020' || item.item_code === 'ITM-021' || item.item_code === 'ITM-022') {
        return item; // Keep culvert quantities as calculated
      }
      return {
        ...item,
        quantity: item.quantity * 1.10, // 10% more for medium
        material_unit_rate: item.material_unit_rate * 0.94 // Better rates for bulk
      };
    });
    return baseTemplate;
  }

  getLargeRoadTemplate(roadLength: number, project: any): any {
    const baseTemplate = this.getSmallRoadTemplate(roadLength, project);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-020' || item.item_code === 'ITM-021' || item.item_code === 'ITM-022') {
        return item;
      }
      return {
        ...item,
        quantity: item.quantity * 1.20,
        material_unit_rate: item.material_unit_rate * 0.90
      };
    });
    return baseTemplate;
  }

  getVeryLargeRoadTemplate(roadLength: number, project: any): any {
    const baseTemplate = this.getSmallRoadTemplate(roadLength, project);
    baseTemplate.items = baseTemplate.items.map((item: any) => {
      if (item.item_code === 'ITM-020' || item.item_code === 'ITM-021' || item.item_code === 'ITM-022') {
        return item;
      }
      return {
        ...item,
        quantity: item.quantity * 1.30,
        material_unit_rate: item.material_unit_rate * 0.87
      };
    });
    return baseTemplate;
  }

  calculateQuantityForRoad(item: any, roadLength: number, project: any): number {
    // If item has a base_quantity_per_km or base_quantity_per_sqm, use it
    if (item.base_quantity_per_km) {
      const isKm = project.road_length || project.roadLength;
      return item.base_quantity_per_km * (isKm ? roadLength : roadLength / 1000);
    }
    
    if (item.base_quantity_per_sqm) {
      const roadWidth = parseFloat(project.road_width) || parseFloat(project.roadWidth) || 7;
      const isKm = project.road_length || project.roadLength;
      const lengthInMeters = isKm ? roadLength * 1000 : Math.sqrt(roadLength);
      const areaInSqm = lengthInMeters * roadWidth;
      return item.base_quantity_per_sqm * areaInSqm;
    }
    
    // Otherwise use the quantity from template (already calculated)
    return item.quantity || 0;
  }

  loadBoQ(): void {
    if (!this.boqId) return;

    this.loading = true;
    this.apiService.getBoQById(this.boqId).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          const boq = res.data;
          
          // Patch basic fields
          this.boqForm.patchValue({
            project_id: boq.project_id,
            boq_code: boq.boq_code,
            boq_name: boq.boq_name,
            description: boq.description,
            currency: boq.currency || 'INR',
            exchange_rate: boq.exchange_rate || 1.0,
            overhead_percentage: boq.overhead_percentage || 0,
            profit_percentage: boq.profit_percentage || 0,
            tax_percentage: boq.tax_percentage || 0,
            contingency_percentage: boq.contingency_percentage || 0,
            branch_id: boq.branch_id
          });

          this.boqMetadata = this.parseMetadata(boq.metadata);
          const lrs = (this.boqMetadata['lrs'] as Record<string, unknown>) || {};
          this.boqForm.patchValue({
            lrs_route_id: lrs['route_id'] != null ? Number(lrs['route_id']) : null,
            lrs_from_km: lrs['from_km'] != null ? Number(lrs['from_km']) : null,
            lrs_to_km: lrs['to_km'] != null ? Number(lrs['to_km']) : null,
            lrs_chainage: lrs['chainage'] || '',
          });
          this.lrsExpanded = !!(lrs['route_id'] || lrs['from_km'] || lrs['to_km'] || lrs['chainage']);

          // Load sections
          if (boq.sections && Array.isArray(boq.sections)) {
            boq.sections.forEach((section: BoQSection) => {
              this.addSection(section);
            });
          }

          // Load items - items can be in sections or itemsWithoutSection
          let allItems: BoQItem[] = [];
          
          // Collect items from sections
          if (boq.sections && Array.isArray(boq.sections)) {
            boq.sections.forEach((section: any) => {
              if (section.items && Array.isArray(section.items)) {
                allItems = allItems.concat(section.items);
              }
            });
          }
          
          // Add items without section
          if (boq.itemsWithoutSection && Array.isArray(boq.itemsWithoutSection)) {
            allItems = allItems.concat(boq.itemsWithoutSection);
          }
          
          // Fallback: if items is a flat array (for backward compatibility)
          if (allItems.length === 0 && boq.items && Array.isArray(boq.items)) {
            allItems = boq.items;
          }
          
          // Add all items to the form
          allItems.forEach((item: BoQItem) => {
            this.addItem(item);
          });

          this.boqDetail = {
            assigned_to: boq.assigned_to,
            assigned_to_name: boq.assigned_to_name,
            estimation_status: boq.estimation_status || 'draft'
          };
          this.assignStaffId = boq.assigned_to || null;
          this.loadReviewRequests();
          this.loadStaffForAssign();
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading BoQ:', err);
        this.toastrService.error('Failed to load BoQ');
        this.loading = false;
      }
    });
  }

  loadReviewRequests(): void {
    if (!this.boqId) return;
    this.apiService.getBoQReviewRequests(this.boqId).subscribe({
      next: (res: any) => {
        this.reviewRequests = (res.success && res.data) ? res.data : [];
      },
      error: () => { this.reviewRequests = []; }
    });
  }

  loadStaffForAssign(): void {
    this.apiService.getStaff({ keyword: '' }).subscribe({
      next: (res: any) => {
        this.staffList = Array.isArray(res) ? res : (res?.data || res || []);
      },
      error: () => { this.staffList = []; }
    });
  }

  assignToManager(): void {
    if (!this.boqId || this.assignStaffId == null) return;
    this.assignSending = true;
    this.apiService.assignBoQToManager(this.boqId, this.assignStaffId).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.boqDetail = { ...this.boqDetail, assigned_to: this.assignStaffId!, estimation_status: 'pending_review' };
          this.boqDetail.assigned_to_name = this.staffList.find(s => s.id === this.assignStaffId)?.full_name || '';
          this.toastrService.success('BOQ assigned to project manager');
        }
        this.assignSending = false;
      },
      error: (err: any) => {
        this.toastrService.error(err.error?.error || 'Failed to assign');
        this.assignSending = false;
      }
    });
  }

  openReviewRequestForm(): void {
    this.showReviewRequestForm = true;
    this.showRejectForm = false;
    this.reviewRequestType = 'update_field';
    this.reviewRequestDescription = '';
  }

  cancelReviewRequestForm(): void {
    this.showReviewRequestForm = false;
  }

  submitReviewRequest(): void {
    if (!this.boqId) return;
    this.reviewRequestSending = true;
    this.apiService.createBoQReviewRequest(this.boqId, {
      request_type: this.reviewRequestType,
      description: this.reviewRequestDescription || undefined
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Revision requested');
          this.boqDetail = { ...this.boqDetail, estimation_status: 'changes_requested' };
          this.loadReviewRequests();
          this.showReviewRequestForm = false;
        }
        this.reviewRequestSending = false;
      },
      error: (err: any) => {
        this.toastrService.error(err.error?.error || 'Failed to submit request');
        this.reviewRequestSending = false;
      }
    });
  }

  approveEstimation(): void {
    if (!this.boqId) return;
    this.approveSending = true;
    this.apiService.approveBoQEstimation(this.boqId).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.boqDetail = { ...this.boqDetail, estimation_status: 'approved' };
          this.toastrService.success('Estimation approved');
        }
        this.approveSending = false;
      },
      error: (err: any) => {
        this.toastrService.error(err.error?.error || 'Failed to approve estimation');
        this.approveSending = false;
      }
    });
  }

  submitForApproval(): void {
    if (!this.boqId) return;
    this.submitSending = true;
    this.apiService.submitBoQForApproval(this.boqId, this.assignStaffId || undefined).subscribe({
      next: (res: any) => {
        if (res.success) {
          const assignedTo = res.data?.assigned_to ?? this.assignStaffId;
          this.boqDetail = {
            ...this.boqDetail,
            assigned_to: assignedTo,
            assigned_to_name: this.staffList.find(s => s.id === assignedTo)?.full_name || this.boqDetail.assigned_to_name,
            estimation_status: 'pending_review'
          };
          this.toastrService.success('Enquiry submitted for approval');
        }
        this.submitSending = false;
      },
      error: (err: any) => {
        this.toastrService.error(err.error?.error || 'Assign a project manager before submitting');
        this.submitSending = false;
      }
    });
  }

  openRejectForm(): void {
    this.showRejectForm = true;
    this.rejectReason = '';
    this.showReviewRequestForm = false;
  }

  cancelRejectForm(): void {
    this.showRejectForm = false;
    this.rejectReason = '';
  }

  rejectEstimation(): void {
    if (!this.boqId || !this.rejectReason.trim()) return;
    this.rejectSending = true;
    this.apiService.rejectBoQEstimation(this.boqId, this.rejectReason.trim()).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.boqDetail = { ...this.boqDetail, estimation_status: 'rejected' };
          this.toastrService.success('Estimation rejected');
          this.cancelRejectForm();
          this.loadReviewRequests();
        }
        this.rejectSending = false;
      },
      error: (err: any) => {
        this.toastrService.error(err.error?.error || 'Failed to reject estimation');
        this.rejectSending = false;
      }
    });
  }

  get canShowApprovalSection(): boolean {
    return this.boqDetail?.estimation_status === 'pending_review';
  }

  get canSubmitForApproval(): boolean {
    const s = this.boqDetail?.estimation_status || 'draft';
    return s === 'draft' || s === 'changes_requested' || s === 'rejected';
  }

  addSection(section?: BoQSection): void {
    const sectionForm = this.fb.group({
      section_id: [section?.section_id || null],
      section_code: [section?.section_code || '', Validators.required],
      section_name: [section?.section_name || '', Validators.required],
      description: [section?.description || ''],
      sequence_order: [section?.sequence_order || this.sections.length + 1],
      parent_section_id: [section?.parent_section_id || null]
    });
    this.sections.push(sectionForm);
  }

  removeSection(index: number): void {
    this.sections.removeAt(index);
    // Update sequence orders
    this.sections.controls.forEach((control, i) => {
      control.patchValue({ sequence_order: i + 1 });
    });
  }

  addItem(item?: BoQItem): void {
    const breakdown = item?.breakdown && Array.isArray(item.breakdown) ? item.breakdown : [];
    const resourceInputs = this.fb.array(
      breakdown.map((r: any) => this.createResourceInputGroup(r))
    );
    const itemForm = this.fb.group({
      item_id: [item?.item_id || null],
      section_id: [item?.section_id || null],
      item_code: [item?.item_code || '', Validators.required],
      item_description: [item?.item_description || '', Validators.required],
      specification: [item?.specification || ''],
      unit: [item?.unit || '', Validators.required],
      quantity: [item?.quantity || 0, [Validators.required, Validators.min(0)]],
      material_unit_rate: [item?.material_unit_rate || 0, Validators.min(0)],
      labor_unit_rate: [item?.labor_unit_rate || 0, Validators.min(0)],
      equipment_unit_rate: [item?.equipment_unit_rate || 0, Validators.min(0)],
      other_costs: [item?.other_costs || 0, Validators.min(0)],
      wastage_percentage: [item?.wastage_percentage || 0, [Validators.min(0), Validators.max(100)]],
      discount_percentage: [item?.discount_percentage || 0, [Validators.min(0), Validators.max(100)]],
      tax_percentage: [item?.tax_percentage || 0, [Validators.min(0), Validators.max(100)]],
      remarks: [item?.remarks || ''],
      sequence_order: [item?.sequence_order || this.items.length + 1],
      resource_inputs: resourceInputs
    });
    this.items.push(itemForm);
  }

  createResourceInputGroup(r?: any): FormGroup {
    const type = r?.breakdown_type || 'material';
    const normalizedType = type === 'labor' ? 'man' : (type === 'equipment' ? 'machine' : type);
    return this.fb.group({
      breakdown_type: [normalizedType, Validators.required],
      description: [r?.description || '', Validators.required],
      quantity: [r?.quantity ?? 0, [Validators.required, Validators.min(0)]],
      unit: [r?.unit || ''],
      unit_rate: [r?.unit_rate ?? 0, [Validators.required, Validators.min(0)]]
    });
  }

  mapResourceTypeToBackend(type: string): string {
    return type === 'man' ? 'labor' : (type === 'machine' ? 'equipment' : type);
  }

  getResourceInputs(itemIndex: number): FormArray {
    return this.items.at(itemIndex).get('resource_inputs') as FormArray;
  }

  addResourceInput(itemIndex: number): void {
    this.getResourceInputs(itemIndex).push(this.createResourceInputGroup());
  }

  removeResourceInput(itemIndex: number, resIndex: number): void {
    this.getResourceInputs(itemIndex).removeAt(resIndex);
  }

  removeItem(index: number): void {
    this.items.removeAt(index);
    // Update sequence orders
    this.items.controls.forEach((control, i) => {
      control.patchValue({ sequence_order: i + 1 });
    });
  }

  getUnitDisplay(unitCode: string): string {
    const unit = this.units.find(u => u.unit_code === unitCode);
    return unit ? `${unit.unit_code} - ${unit.unit_name}` : unitCode;
  }

  filterUnitsByType(type: string): void {
    if (type === 'all') {
      this.filteredUnits = this.units;
    } else {
      this.filteredUnits = this.units.filter(u => u.unit_type === type);
    }
  }

  onSubmit(): void {
    if (this.boqForm.invalid) {
      this.toastrService.error('Please fill all required fields');
      this.boqForm.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const formValue = this.boqForm.value;
    const { lrs_route_id, lrs_from_km, lrs_to_km, lrs_chainage, ...rest } = formValue;

    const boqData = {
      ...rest,
      metadata: {
        ...this.boqMetadata,
        lrs: this.isRoadDomain && lrs_route_id
          ? {
              route_id: lrs_route_id,
              from_km: lrs_from_km,
              to_km: lrs_to_km,
              chainage: lrs_chainage || undefined,
            }
          : (this.isRoadDomain ? this.boqMetadata['lrs'] : undefined),
      },
      sections: formValue.sections.map((s: any) => ({
        section_id: s.section_id,
        section_code: s.section_code,
        section_name: s.section_name,
        description: s.description,
        sequence_order: s.sequence_order,
        parent_section_id: s.parent_section_id || null
      })),
      items: formValue.items.map((item: any) => ({
        item_id: item.item_id,
        section_id: item.section_id || null,
        item_code: item.item_code,
        item_description: item.item_description,
        specification: item.specification,
        unit: item.unit,
        quantity: parseFloat(item.quantity) || 0,
        material_unit_rate: parseFloat(item.material_unit_rate) || 0,
        labor_unit_rate: parseFloat(item.labor_unit_rate) || 0,
        equipment_unit_rate: parseFloat(item.equipment_unit_rate) || 0,
        other_costs: parseFloat(item.other_costs) || 0,
        wastage_percentage: parseFloat(item.wastage_percentage) || 0,
        discount_percentage: parseFloat(item.discount_percentage) || 0,
        tax_percentage: parseFloat(item.tax_percentage) || 0,
        remarks: item.remarks,
        sequence_order: item.sequence_order,
        breakdown: (item.resource_inputs || []).filter((r: any) => r.description?.trim()).map((r: any) => ({
          breakdown_type: this.mapResourceTypeToBackend(r.breakdown_type || 'material'),
          description: r.description?.trim() || '',
          quantity: parseFloat(r.quantity) || 0,
          unit: r.unit || null,
          unit_rate: parseFloat(r.unit_rate) || 0
        }))
      }))
    };

    if (this.mode === 'create') {
      this.apiService.createBoQ(boqData).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('BoQ created successfully');
            this.router.navigate(['/home/boq']);
          } else {
            this.toastrService.error(res.error || 'Failed to create BoQ');
          }
          this.submitting = false;
        },
        error: (err: any) => {
          console.error('Error creating BoQ:', err);
          this.toastrService.error(err.error?.error || 'Failed to create BoQ');
          this.submitting = false;
        }
      });
    } else {
      if (!this.boqId) return;
      this.apiService.updateBoQ(this.boqId, boqData).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('BoQ updated successfully');
            this.router.navigate(['/home/boq']);
          } else {
            this.toastrService.error(res.error || 'Failed to update BoQ');
          }
          this.submitting = false;
        },
        error: (err: any) => {
          console.error('Error updating BoQ:', err);
          this.toastrService.error(err.error?.error || 'Failed to update BoQ');
          this.submitting = false;
        }
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/home/boq']);
  }

  calculateItemTotal(index: number): number {
    const item = this.items.at(index);
    const quantity = parseFloat(item.get('quantity')?.value) || 0;
    const materialRate = parseFloat(item.get('material_unit_rate')?.value) || 0;
    const laborRate = parseFloat(item.get('labor_unit_rate')?.value) || 0;
    const equipmentRate = parseFloat(item.get('equipment_unit_rate')?.value) || 0;
    const otherCosts = parseFloat(item.get('other_costs')?.value) || 0;
    
    const unitRate = materialRate + laborRate + equipmentRate + otherCosts;
    const lineTotal = quantity * unitRate;
    
    // Apply wastage
    const wastagePct = parseFloat(item.get('wastage_percentage')?.value) || 0;
    const afterWastage = lineTotal * (1 + wastagePct / 100);
    
    // Apply discount
    const discountPct = parseFloat(item.get('discount_percentage')?.value) || 0;
    const afterDiscount = afterWastage * (1 - discountPct / 100);
    
    // Apply tax
    const taxPct = parseFloat(item.get('tax_percentage')?.value) || 0;
    const finalTotal = afterDiscount * (1 + taxPct / 100);
    
    return finalTotal;
  }

  get itemsSubtotal(): number {
    return this.items.controls.reduce((sum, _, i) => sum + this.calculateItemTotal(i), 0);
  }

  get boqGrandTotal(): number {
    const overhead = parseFloat(this.boqForm?.get('overhead_percentage')?.value) || 0;
    const profit = parseFloat(this.boqForm?.get('profit_percentage')?.value) || 0;
    const tax = parseFloat(this.boqForm?.get('tax_percentage')?.value) || 0;
    const contingency = parseFloat(this.boqForm?.get('contingency_percentage')?.value) || 0;
    let total = this.itemsSubtotal;
    total *= 1 + overhead / 100;
    total *= 1 + profit / 100;
    total *= 1 + tax / 100;
    total *= 1 + contingency / 100;
    return total;
  }

  formatInr(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  }
}
